import React, { useEffect, useState } from "react";
import {
  Form, Input, DatePicker, TimePicker, Select, Mentions, Button, message,
  Checkbox, Radio, Modal, Table
} from 'antd';
import dayjs from 'dayjs';
import getOffsetTime from '../../../../utils/getOffsetTime.js';
import FindCostObject from '../../SelectCostObject/FindCostObject.js';
import AuthLogic from '../../../../AuthLogic/index.js';
import './forms.scss';

const { TextArea } = Input;
const { Option } = Select;

const datePickerFormat = 'MM/DD/YYYY'
const timePickerFormat = 'HH:mm';
const FormItem = Form.Item;

const EditAssignmentDetailsForm = React.memo(({ loading, resource, itemDetails, user, updatingUser,
  job, costObject, approvedJobElements, jobElements, ...props
}) => {
  const { getFieldDecorator, getFieldsError, getFieldsValue } = props.form;
  const [showFindCostObjectForSelectionModal, setShowFindCostObjectForSelectionModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);


  const invalidForm = () => {
    message.config({
      top: 150,
      duration: 5,
      maxCount: 5,
    });
    message.error('You have one or more invalid form inputs!');
  };

  const handleSubmit = async (e) => {
    setFormSubmitted(true);
    e.preventDefault();
    const errors = {};
    // collect all fields currently in error state
    for (const [field, error] of Object.entries(getFieldsError())) {
      if (error) {
        errors[`${field}`] = error;
      }
    }
    if (Object.values(errors).length === 0) {
      console.log('Success! Received values of form: ', getFieldsValue())
      props.updateJob();
    } else {
      invalidForm();
      console.log('Invalid Form! ', errors);
    }
  };

  const validateTitleField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 50) {
      callback('Maximum length is 50 characters!');
    } else {
      callback();
    }
  }

  const validateJobBaseDate = (rule, value, callback) => {
    const form = props.form;
    if (!value) {
      callback('Please input a date!');
    }
  }

  const validateCommentField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 100) {
      callback('Maximum length is 100 characters!');
    } else {
      callback();
    }
  }

  const validateAdditionalInformationField = (rule, value, callback) => {
    const form = props.form;
    if (value.length > 8000) {
      callback('Maximum length is 8000 characters!')
    } else {
      callback();
    }
  }

  const handleSelectCostObjectClick = () => {
    setShowFindCostObjectForSelectionModal(true);
  }

  const handleSelectCostObject = (record) => {
    // input costObjectTypeId field value
    record.costObjectTypeId = record.costObjectType === 'COST_CENTER' ? 2 : 1; // TODO: fix findCostObject so PROJECT_DEFINITION isn't possible
    itemDetails.costObject = {
      costObjectId: record.id,
      costObjectTypeId: record.costObjectTypeId,
      costObjectName: record.costObjectName,
      costObjectNumber: record.costObjectNumber,
      costObjectType: record.costObjectType,
      wbsElementCategory: record.wbsElementCategory
    }
    // flag costObject change so that jobElements can be updated if user chooses to
    itemDetails.costObjectChange = true;
    props.updateState({ itemDetails });
  }

  const handleSelectCostObjectModalCancel = () => {
    setShowFindCostObjectForSelectionModal(false);
  }

  // jobBaseDate update hook
  useState(() => {
    // timezone offset adjustment
    let jobBaseDate = dayjs(job.baseDate);
    let timeZoneOffset = getOffsetTime(jobBaseDate);
    jobBaseDate = jobBaseDate.add(timeZoneOffset, 'minute');
    itemDetails.jobBaseDate = jobBaseDate
    props.updateState({ itemDetails });
  }, [itemDetails, /* itemDetails.jobBaseDate */, job])

  useState(() => {
    // insert job details into itemDetails
    itemDetails.itemType = 'job';
    itemDetails.GTitle = job.GTitle;
    itemDetails.GAdditionalInformation = job.GAdditionalInformation;
    itemDetails.GSchedulerComments = job.GSchedulerComments;
    itemDetails.jobNumber = job.jobNumber;
    itemDetails.creatingUserId = job.creatingUserId;
    itemDetails.costObject = {
      costObjectId: job.costObjectId,
      costObjectTypeId: job.costObjectTypeId,
      costObjectName: costObject.costObjectName,
      costObjectType: costObject.costObjectType,
      costObjectNumber: costObject.costObjectNumber
    };
    itemDetails.startRequest = job.startRequest;
    itemDetails.endRequest = job.endRequest;
    itemDetails.updatingUserId = updatingUser.updatingUserId;
    // no matter what, update state
    props.updateState({ itemDetails });

  }, [job]);

  //form field hooks
  useEffect(() => {
    const form = props.form;
    if (approvedJobElements && itemDetails.jobBaseDate) {
      /* Note: getApprovedJobElements is the only api that fires once this tab loads
       hence its completion is a quick way to delay this hook until all form fields have rendered
      */
      form.setFieldsValue({
        jobBaseDate: dayjs(itemDetails.jobBaseDate),
        GTitle: itemDetails.GTitle,
        GAdditionalInformation: itemDetails.GAdditionalInformation,
        GSchedulerComments: itemDetails.GSchedulerComments,
      });
    }
  }, [approvedJobElements, itemDetails, itemDetails.jobStartTime]);

  // only call these if they are not already in state
  useEffect(() => {
    if (!approvedJobElements) {
      props.getApprovedJobElements();
    }
    if (!jobElements) {
      props.getJobElements();
    }
  }, [approvedJobElements, jobElements]);


  if (itemDetails && job && approvedJobElements && !loading) {
    console.log(job.baseDate); // debug

    // format job.baseDate into (MM/DD/YYYY)
    let baseDate;
    if (job.baseDate) {
      baseDate = job.baseDate.split('T')[0];
      baseDate = baseDate.split('-');
      baseDate = `${baseDate[1]}/${baseDate[2]}/${baseDate[0]}`;
    }

    // adjust startRequest and endRequest per timezone for text
    let timeZoneOffset = getOffsetTime(dayjs(job.baseDate));
    let startRequest = dayjs(job.startRequest).add(timeZoneOffset, 'minute');
    let endRequest = dayjs(job.endRequest).add(timeZoneOffset, 'minute');

    return (

      <Form onSubmit={handleSubmit}>
        <div>
          Request Date: <b><u>{baseDate}</u></b> <br />
          {`Request #: ${job.jobNumber}: ${job.name}`}<br />
          {`Cost Object #: ${costObject.costObjectNumber} ${costObject.costObjectName} ${costObject.costObjectType}`}<br />
          {`Request Date Range: ${dayjs(startRequest).format('MM/DD/YYYY')} - ${dayjs(endRequest).format('MM/DD/YYYY')}`}<br />
          {`Modified By: ${updatingUser.name}`}
        </div>
        <br></br>

        <FormItem
          label="Title"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('GTitle', {
            rules: [{
              required: true, message: 'Please input a title!',
            }, {
              validator: validateTitleField,
            }],
          })(
            <Input
              name="GTitle"
              onChange={props.handleOmnibusFieldChange}
              size={'large'}
              placeholder={'Enter title here.'}
              style={{ width: 300 }} />
          )}
        </FormItem>

        <FormItem
          label={<span><span id="asterisk"> </span><span>Date</span></span>}
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('jobBaseDate', {
            rules: [{
              // required: true, message: 'Please input a date!',
            }, {
              validator: validateJobBaseDate,
            }],
          })(
            <DatePicker
              name="jobBaseDate"
              // showTime={{ format: datePickerFormat, use12Hours: true, minuteStep: 15 }}
              onChange={props.handleOmnibusFieldChange}
              placeholder={'Select date'}
              format={datePickerFormat}
              allowClear={false}
              size={'large'}
              styles={{
                popup: {
                  root: {
                    zIndex: 2000
                  }
                }
              }}
            />
          )}
        </FormItem>

        <FormItem
          label="Additional Information"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('GAdditionalInformation', {
            rules: [{
              validator: validateAdditionalInformationField,
            }],
          })(
            <TextArea
              name="GAdditionalInformation"
              onChange={props.handleOmnibusFieldChange}
              size={'large'}
              placeholder={'Enter text here.'}
              style={{ width: 300 }} />
          )}
        </FormItem>

        <FormItem
          label="Scheduler Comments"
          style={{ display: "flex", marginBottom: 5 }}
        >
          {getFieldDecorator('GSchedulerComments', {
            rules: [{
              validator: validateCommentField,
            }],
          })(
            <TextArea
              name="GSchedulerComments"
              onChange={props.handleOmnibusFieldChange}
              size={'large'}
              placeholder={'Enter text here.'}
              style={{ width: 300 }} />
          )}
        </FormItem>

        <div
          style={{ display: "flex", marginBottom: 5 }}
        >
          <span id="asterisk">* </span>
          <a
            onClick={() => handleSelectCostObjectClick()}
            style={{ color: 'dodgerblue', textDecoration: 'underline', cursor: 'pointer' }}
          >
            Cost Object/Type:
          </a>

          {`\xa0`}{`\xa0`}{`\xa0`}
          {itemDetails.costObject &&
            `${itemDetails.costObject.costObjectName}
                        ${`\xa0`}
                        ${itemDetails.costObject.costObjectType === 'WBS_ELEMENT'
              || itemDetails.costObject.costObjectType === 'PROJECT_DEFINITION'
              ? 'WBS Element' : itemDetails.costObject.costObjectType || 'Not specified'}`}
          {`\xa0`}

          {showFindCostObjectForSelectionModal &&
            <Modal
              open={showFindCostObjectForSelectionModal}
              title={'Find Cost Object'}
              onCancel={handleSelectCostObjectModalCancel}
              footer={[
              ]}
            >
              <FindCostObject
                requestDate={itemDetails.selectedJobElementDetails.startActual}
                handleSelectCostObject={handleSelectCostObject}
                handleModalCancel={handleSelectCostObjectModalCancel}
              />
            </Modal>
          }
        </div>

        <FormItem
          label="Apply Cost Object Change to All Services"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Checkbox
            name="applyCostObjectChangeToAllServices"
            onChange={props.handleOmnibusFieldChange}
            size={'large'}
            disabled={approvedJobElements.counter > 0 ? true : false}
          />
        </FormItem>

        <FormItem
          label="Delete Tours"
          style={{ display: "flex", marginBottom: 5 }}
        >
          <Checkbox
            name="deleteTours"
            onChange={props.handleOmnibusFieldChange}
            size={'large'}
          />

          <Button type="danger" ghost size={'small'}
            style={{ marginLeft: 25 }}
            onClick={() => props.handleItemDelete('job')}
          >
            Delete Request
          </Button>
        </FormItem>

        <FormItem
          style={{ marginTop: 20 }}
        >
          <Button type="primary" ghost block size={'large'} htmlType="submit" disabled={formSubmitted} >
            {!formSubmitted ? `Update` : 'Updating...'}
          </Button>
        </FormItem>

      </Form>
    );
  } else return 'loading...'
})

export default EditAssignmentDetailsForm;

